import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { regionService } from '@/services/regions';
import { toast } from 'sonner';
import type {
  Region,
  CreateRegionInput,
  UpdateRegionInput,
} from '@/features/regions/types';

/**
 * Hook to get all regions for a provider
 */
export function useRegions(providerId: number) {
  return useQuery({
    queryKey: ['regions', providerId],
    queryFn: () => regionService.getRegions(providerId),
    enabled: !!providerId
  });
}

/**
 * Hook to get regions with version counts for a provider
 */
export function useRegionsWithCounts(providerId: number) {
  return useQuery({
    queryKey: ['regions-with-counts', providerId],
    queryFn: () => regionService.getRegionsWithCounts(providerId),
    enabled: !!providerId
  });
}

/**
 * Hook to get a single region by ID
 */
export function useRegion(id: number) {
  return useQuery({
    queryKey: ['region', id],
    queryFn: () => regionService.getRegionById(id),
    enabled: !!id
  });
}

/**
 * Hook to find regions by zip code
 */
export function useFindRegionsByZipCode(zipCode: string) {
  return useQuery({
    queryKey: ['regions-by-zip', zipCode],
    queryFn: () => regionService.findRegionsByZipCode(zipCode),
    enabled: !!zipCode && zipCode.trim().length > 0,
    // Don't refetch automatically, only when zip code changes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });
}

/**
 * Hook to create a new region
 */
export function useCreateRegion() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateRegionInput) => regionService.createRegion(data),
    onSuccess: (region: Region) => {
      // Invalidate the regions queries for this provider
      queryClient.invalidateQueries({ queryKey: ['regions', region.provider_id] });
      queryClient.invalidateQueries({ queryKey: ['regions-with-counts', region.provider_id] });
      
      toast.success(`Region "${region.name}" has been created successfully.`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'An unexpected error occurred');
    }
  });
}

/**
 * Hook to update a region
 */
export function useUpdateRegion() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number, data: UpdateRegionInput }) => 
      regionService.updateRegion(id, data),
    onSuccess: (region: Region) => {
      // Invalidate the region and regions queries
      queryClient.invalidateQueries({ queryKey: ['region', region.id] });
      queryClient.invalidateQueries({ queryKey: ['regions', region.provider_id] });
      queryClient.invalidateQueries({ queryKey: ['regions-with-counts', region.provider_id] });
      
      toast.success(`Region "${region.name}" has been updated successfully.`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'An unexpected error occurred');
    }
  });
}

/**
 * Hook to delete a region
 */
export function useDeleteRegion() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, providerId }: { id: number, providerId: number }) => {
      await regionService.deleteRegion(id);
      return { id, providerId };
    },
    onSuccess: ({ providerId }) => {
      // Invalidate the regions queries for this provider
      queryClient.invalidateQueries({ queryKey: ['regions', providerId] });
      queryClient.invalidateQueries({ queryKey: ['regions-with-counts', providerId] });
      
      toast.success('The region has been deleted successfully.');
    },
    onError: (error: any) => {
      toast.error(error.message || 'An unexpected error occurred');
    }
  });
}
